import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { ToastModule } from 'primeng/toast';
import { BesharpTableComponent, BesharpTableHeaderDefinition, BesharpTableActionDefinition } from '../../modules/besharp-table/component/besharp-table.component';
import { ProviderService } from '../../services/provider.service';
import { Machine } from '../../services/machine-list.service';
import { MessageService } from 'primeng/api';

@Component({
  selector: 'app-machine-list',
  templateUrl: './machine-list.component.html',
  styleUrl: './machine-list.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    InputTextModule,
    ButtonModule,
    IconFieldModule,
    InputIconModule,
    ToastModule,
    BesharpTableComponent
  ]
})
export class MachineListComponent implements OnInit {
  machines: Machine[] = [];
  allMachines: Machine[] = [];
  totalCount = 0;
  loading = false;
  settingUpSerial: string | null = null;
  searchFilter = '';
  currentPage = 0;
  pageSize = 10;
  hasNextPage = true;

  tableDefinitions: BesharpTableHeaderDefinition[] = [
    { 
      def: 'serial', 
      title: 'Serial', 
      sortable: true,
      columnFilter: false,
    },
    { 
      def: 'name', 
      title: 'Name', 
      sortable: true,
      columnFilter: false,
    },
    { 
      def: 'swVersion', 
      title: 'SW Version', 
      sortable: true,
      columnFilter: false,
    },
    { 
      def: 'lastConnection', 
      title: 'Last Connection', 
      sortable: true,
      columnFilter: false,
      transformer: (value: string) => this.formatDate(value),
    },
    { 
      def: 'isConnected',
      title: 'Connected', 
      sortable: true,
      columnFilter: false,
      transformer: (value: boolean) => value ? '<span class="status-badge status-success"><i class="pi pi-check-circle"></i> Online</span>' : '<span class="status-badge status-danger"><i class="pi pi-times-circle"></i> Offline</span>',
    },
    {
      def: 'isSetup',
      title: 'VPN Configured',
      sortable: false,
      columnFilter: false,
      transformer: (value: boolean) => value ? '<span class="status-badge status-success"><i class="pi pi-check-circle"></i> Configured</span>' : '<span class="status-badge status-danger"><i class="pi pi-times-circle"></i> Not configured</span>',
    },
  ];

  baseActionDefinitions: BesharpTableActionDefinition[] = [
    {
      icon: 'pi pi-play',
      label: (row: Machine) => row.isSetup ? 'Re-setup Machine' : 'Setup Machine',
      styleClass: 'p-button-outlined',
      callback: (row: Machine) => this.setupMachine(row.serial, row.isSetup),
      tooltip: 'Setup machine',
      active: (row: Machine) => true,
    },
    {
      icon: 'pi pi-trash',
      label: 'Delete OpenVPN profile',
      styleClass: 'p-button-outlined p-button-text',
      callback: (row: Machine) => this.deleteOpenVPNProfile(row.serial),
      tooltip: 'Delete OpenVPN profile',
    },
  ];

  actionDefinitions: BesharpTableActionDefinition[] = [];

  constructor(private providerService: ProviderService, private messageService: MessageService) {
  }

  ngOnInit() {
    this.loadMachines();
    this.updateActionDefinitions();
  }

  updateActionDefinitions() {}

  getFilteredActions(): BesharpTableActionDefinition[] {
    return this.baseActionDefinitions;
  }

  loadMachines(page: number = 0, pageSize: number = this.pageSize, serial?: string) {
    this.loading = true;
    this.machines = [];
    this.providerService.machineListService.getMachines(page, pageSize, serial).subscribe({
      next: (machines) => {
        this.allMachines = machines;
        this.machines = machines;
        this.currentPage = page;
        this.pageSize = pageSize;
        this.hasNextPage = machines.length.toString() === pageSize.toString(); 
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading machines:', error);
        this.allMachines = [];
        this.machines = [];
        this.hasNextPage = false;
        this.loading = false;
      }
    });
  }

  setupMachine(serial: string, isSetup: boolean) {
    this.settingUpSerial = serial;
    this.loading = true;
    this.providerService.machineListService.setupMachine(serial, isSetup).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Machine setup initiated',
          life: 2500
        });
        this.loadMachines();
        this.settingUpSerial = null;
      },
      error: () => {
        this.loading = false;
        this.settingUpSerial = null;
      }
    });
  }

  handleSetupMachine(machine: Machine) {
    if (machine.isConnected) {
      return;
    }
    this.setupMachine(machine.serial, machine.isSetup);
  }

  deleteOpenVPNProfile(serial: string) {
    this.loading = true;
    this.providerService.machineListService.deleteOpenVPNProfile(serial).subscribe({ next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'OpenVPN profile deleted',
          life: 2500
        });
        this.loadMachines();
      }, error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'OpenVPN profile not deleted',
          life: 2500
        });
        this.loading = false;
        this.loadMachines();
      }
    });
  }

  onSearch(searchTerm: string): void {
    this.currentPage = 0;
    this.hasNextPage = true;
    
    console.log('Starting search for:', searchTerm);
    
    if (searchTerm && searchTerm.trim()) {
      this.loadMachines(0, this.pageSize, searchTerm.trim());
    } else {
      this.loadMachines(0, this.pageSize);
    }
  }

  clearSearch(): void {
    this.searchFilter = '';
    this.onSearch('');
  }

  onRowClicked(row: Machine): void {
    console.log('Row clicked:', row);
  }

  formatDate(dateString: string): string {
    if (!dateString) return '-'; 
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return date.toLocaleDateString('en-EN', {
        day: '2-digit',
        month: '2-digit', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  }

  onPageChange(event: any): void {
    this.currentPage = event.page;
    this.pageSize = event.rows;
    this.searchFilter = '';
    this.loadMachines(this.currentPage, this.pageSize);
  }

  goToNextPage(): void {
    if (this.hasNextPage && !this.loading) {
      const nextPage = this.currentPage + 1;
      this.loading = true;
      this.machines = [];
      const searchTerm = this.searchFilter && this.searchFilter.trim() ? this.searchFilter.trim() : undefined;
      this.providerService.machineListService.getMachines(nextPage, this.pageSize, searchTerm).subscribe({
        next: (machines) => {
          if (machines.length === 0) {
            this.hasNextPage = false;
            this.loading = false;
          } else {
            this.allMachines = machines;
            this.machines = machines;
            this.currentPage = nextPage;
            this.hasNextPage = machines.length.toString() === this.pageSize.toString(); 
            this.loading = false;
          }
        },
        error: (error) => {
          this.hasNextPage = false;
          this.loading = false;
        }
      });
    }
  }

  goToPreviousPage(): void {
    if (this.currentPage > 0 && !this.loading) {
      const searchTerm = this.searchFilter && this.searchFilter.trim() ? this.searchFilter.trim() : undefined;
      this.loadMachines(this.currentPage - 1, this.pageSize, searchTerm);
    }
  }

  onPageSizeChange(): void {
    this.currentPage = 0;
    this.searchFilter = '';
    this.hasNextPage = true;
    this.loadMachines(0, this.pageSize);
  }
}
